import * as React from 'react';
import {Component} from 'react';
import Scrollbars from 'react-custom-scrollbars';
import {observer} from 'mobx-react';
import DraggableList from './DraggableList';
import {isObservableArray} from 'mobx';

interface ScrollProps {
    autoHeight?: boolean;
    autoHeightMax?: number;
    autoHeightMin?: number;
    gradientScroll?: boolean;
    draggable?: boolean;
    handleDNDFinish?: (item: any, destinationIdx: number) => void;
}

export interface ListProps<T> extends ScrollProps {
    data?: Array<T> | {[groupkey: string]: Array<T>};
    renderItem(item: T): JSX.Element;
    renderGroupHeader?(groupKey: string);
    renderGroup?(children: React.ReactChild);
    className?: string;
    keyExtractor?: (item: T) => string;
    onScrollToEnd?: () => void;
    itemProps?: any;
    /** use to send props other than data that shoud effect list rerender */
    extraData?: any;
}

interface ScrollState {
    scrolledToEnd: boolean;
    draggedId: string;
}

@observer
export default class List extends Component<ListProps<any>, ScrollState> {
    scrollbar;

    constructor(props) {
        super(props);
        this.state = {
            scrolledToEnd: false,
            draggedId: null
        };
        this.checkScroll = this.checkScroll.bind(this);
    }

    componentDidMount() {
        this.props.gradientScroll && this.checkScroll();
    }

    componentDidUpdate(prevProps) {
        const {data, gradientScroll} = this.props;
        if (gradientScroll && data.length !== prevProps.data.length) {
            this.checkScroll();
        }
    }

    checkScroll() {
        const {onScrollToEnd, gradientScroll} = this.props;
        /**
         * 1. The scrollHeight value is equal to the minimum height the element would require
         *    in order to fit all the content in the viewport without using a vertical scrollbar.
         * 2. An element's scrollTop value is a measurement of the distance from the element's top
         *    to its topmost visible content.
         * 3. Element.clientHeight is the inner height of an element in pixels
         * 4. delta is a safety area height. It is used to avoid precise check of the scrollEnd
         *    and marks the area which is interpreted as the end of the scroll
         */
        const delta = 10;
        if (
            this.scrollbar.getScrollHeight() - delta <
            this.scrollbar.getScrollTop() + this.scrollbar.getClientHeight()
        ) {
            onScrollToEnd && onScrollToEnd();
            gradientScroll && this.setState({scrolledToEnd: true});
        } else {
            gradientScroll && this.setState({scrolledToEnd: false});
        }
    }

    onScroll = () => {
        const {onScrollToEnd, gradientScroll} = this.props;
        if (gradientScroll || onScrollToEnd) {
            this.checkScroll();
        }
    };

    renderList(data, listIndex) {
        const {keyExtractor, renderItem, itemProps} = this.props;

        return data.map((item, idx) => {
            // TODO: get rid of calculating index once the navigation is handles inside List
            const index = this.calculateIndex(idx, listIndex);
            const key = keyExtractor ? keyExtractor(item) : idx;
            return renderItem({
                item,
                isLastIndex: idx === data.length - 1,
                index,
                key: key,
                ...itemProps
            });
        });
    }

    calculateIndex = (idx, listIndex?) => {
        const {data} = this.props;
        if (listIndex > 0) {
            const key = Object.keys(data)[listIndex - 1];
            return data[key].length + idx;
        }
        return idx;
    };
    renderDataArray(data, listIndex?) {
        return this.props.draggable ? (
            <DraggableList {...this.props} data={data} />
        ) : (
            this.renderList(data, listIndex)
        );
    }

    renderDataObject() {
        const {data, renderGroupHeader, renderGroup} = this.props;
        return Object.keys(data).map((k, i) => {
            return (
                renderGroup &&
                renderGroup(
                    <>
                        {renderGroupHeader && renderGroupHeader(k)}
                        {this.renderDataArray(data[k], i)}
                    </>
                )
            );
        });
    }

    render() {
        const {className, autoHeight, autoHeightMax, autoHeightMin, gradientScroll, data} =
            this.props;
        return (
            <div className="scroll-wrapper">
                <Scrollbars
                    ref={s => (this.scrollbar = s)}
                    onScroll={this.onScroll}
                    autoHeight={autoHeight}
                    autoHeightMax={autoHeightMax}
                    autoHeightMin={autoHeightMin}
                    className={`scroll-area ${className}`}
                    renderThumbVertical={props => <div {...props} className="thumb-vertical" />}
                    //autoHide
                >
                    {Array.isArray(data) || isObservableArray(data)
                        ? this.renderDataArray(data)
                        : this.renderDataObject()}
                </Scrollbars>
                {gradientScroll && !this.state.scrolledToEnd && <div className="overlay"></div>}
            </div>
        );
    }
}
